import React from 'react';
import { Stack, Typography, List } from '@mui/material';
import PageContainer from 'pages/PageContainer';
import {
  DataRetentionTable,
  PrivacyPolicyLists,
  SubProcessorsTable,
} from 'shared/constants/legal-pages-constants/privacy-policy-constants';
import { HomeLink } from 'components/LegalPagesLinks/HomeLink';
import { MailToLink } from 'components/LegalPagesLinks/MailToLink';
import { CustomListItem } from 'components/List/CustomListItem/CustomListItem';
import { LegalTable } from 'components/Table/LegalTable';

const PrivacyPolicy: React.FunctionComponent = () => (
  <PageContainer showMenu={false}>
    <Stack py={{ xs: 0, md: 4 }} rowGap="64px" width="100%" maxWidth="1200px" mx="auto">
      <Typography variant="h1">Privacy Policy</Typography>
      <Stack rowGap="24px">
        <Typography variant="body2">Effective Date: March 13, 2026 • Version 1.1</Typography>
        <Typography variant="body2">
          {'Deb8.TV ("we," "our," or "us") operates the '}
          <HomeLink />
          {` online video debate platform. This Privacy Policy explains how we collect, use, and share
          personal information when you use our website, mobile applications, and related services
          (collectively, the "Services").`}
        </Typography>
        <Typography variant="body2">
          We are committed to protecting your privacy and handling your information responsibly. By
          using our Services, you agree to the practices described in this Policy.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          1. Information We Collect
        </Typography>
        <Typography variant="body2">
          We collect the following categories of personal information:
        </Typography>
        <List className="list-legal-pages">
          {PrivacyPolicyLists.InformationWeCollect.map((item, index) => (
            <CustomListItem key={index} {...item} />
          ))}
        </List>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          2. How We Use Information
        </Typography>
        <Typography variant="body2">We use your information to:</Typography>
        <List className="list-legal-pages">
          {PrivacyPolicyLists.HowWeUseInformation.map((item, index) => (
            <CustomListItem key={index} {...item} />
          ))}
        </List>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          3. Sharing of Information
        </Typography>
        <Typography variant="body2">
          We do not sell personal information. We may share information in the following situations:
        </Typography>
        <List className="list-legal-pages">
          {PrivacyPolicyLists.SharingOfInformation.map((item, index) => (
            <CustomListItem key={index} {...item} />
          ))}
        </List>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          4. Cookies and Tracking
        </Typography>
        <Typography variant="body2">We use cookies and similar technologies to: </Typography>
        <List className="list-legal-pages">
          {PrivacyPolicyLists.CookiesAndTracking.map((item, index) => (
            <CustomListItem key={index} {...item} />
          ))}
        </List>
        <Typography variant="body2">
          You may control cookies through your browser settings, but disabling them may limit the
          functionality of our Services.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          5. Your Rights
        </Typography>
        <Typography variant="body2">
          Depending on your location, you may have rights such as:
        </Typography>
        <List className="list-legal-pages">
          {PrivacyPolicyLists.YourRights.map((item, index) => (
            <CustomListItem key={index} {...item} />
          ))}
        </List>
        <Typography variant="body2">
          {'To exercise these rights, email us at '}
          <MailToLink />
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          6. Children’s Privacy
        </Typography>
        <Typography variant="body2">
          Our Services are not directed to children under 13. We do not knowingly collect personal
          information from children. If we learn that a child's information has been collected, we
          will delete it.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          7. Data Retention
        </Typography>

        <Typography variant="body2">
          We retain different categories of personal data for the following periods:
        </Typography>

        <LegalTable {...DataRetentionTable} />

        <Stack rowGap="24px">
          <Typography variant="body2">
            <strong>Deletion Requests. </strong>
            Users may request deletion of their debate recordings at any time by emailing{' '}
            <MailToLink />. We will complete the deletion within 30 days of receipt. Where a debate
            involves multiple participants, any single participant's deletion request is sufficient
            not require consent from all participants to honor it.
          </Typography>
        </Stack>

        <Typography variant="body2">
          <strong>Institutional Debates. </strong> For debates conducted under an institutional
          partnership agreement, retention periods may be governed by the applicable agreement,
          which may specify different retention windows.
        </Typography>

        <Typography variant="body2">
          <strong>Inactive Accounts. </strong>
          Accounts inactive for 24 consecutive months may have their associated debate recordings
          purged. We will provide 60 days advance notice before any such purge.
        </Typography>

        <Typography variant="body2">
          <strong>Purge Process. </strong>
          Upon expiration of the applicable retention period or upon a valid deletion request,
          recordings are permanently deleted from our primary storage and all backup systems within
          30 days.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          8. Security
        </Typography>
        <Typography variant="body2">
          We implement reasonable safeguards to protect your personal information, including:
        </Typography>
        <List className="list-legal-pages">
          {PrivacyPolicyLists.Security.map((item, index) => (
            <CustomListItem key={index} {...item} />
          ))}
        </List>
        <Typography variant="body2">
          However, no system is 100% secure. We cannot guarantee absolute security, and we encourage
          you to use strong passwords and protect your account credentials.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          9. Sub-Processors
        </Typography>

        <Typography variant="body2">
          We use the following key third-party service providers ("sub-processors") to operate our
          platform:
        </Typography>

        <LegalTable {...SubProcessorsTable} />

        <Stack rowGap="24px">
          <Typography variant="body2">
            For the most current sub-processor list or to request more detail, contact{' '}
            <MailToLink />.
          </Typography>
        </Stack>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          10. Third-Party Links
        </Typography>
        <Typography variant="body2">
          Our Services may link to third-party websites or services. We are not responsible for
          their privacy practices. Please review their policies independently.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          11. Institutional Partners
        </Typography>
        <Typography variant="body2">
          Institutional partners may contact us at <MailToLink /> to discuss custom data processing
          agreements.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          12. Governing Law
        </Typography>
        <Typography variant="body2">
          This Privacy Policy is governed by the laws of the State of Delaware, United States,
          without regard to its conflict of law principles.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          13. Changes to This Policy
        </Typography>
        <Typography variant="body2">
          We may update this Privacy Policy from time to time. If we make material changes, we will
          notify you by posting the updated policy with a new effective date and version number.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          14. Contact Us
        </Typography>
        <Typography variant="body2">
          If you have questions or requests regarding this Privacy Policy, contact us at:
        </Typography>
        <Typography variant="body2">
          <strong>Deb8.TV</strong> Email: <MailToLink />
        </Typography>
      </Stack>
      <Typography variant="body2">Privacy Policy v1.1 • Deb8.TV • © 2025–2026</Typography>
    </Stack>
  </PageContainer>
);

export default PrivacyPolicy;
